import { useEffect, useRef } from "react";
import * as Tone from "tone";
import { Transport } from "./components/Transport/Transport";
import { DrumTrack } from "./components/DrumTrack/DrumTrack";
import { PianoRollTrack } from "./components/PianoRollTrack/PianoRollTrack";
import { AdvancedPanel } from "./components/AdvancedPanel/AdvancedPanel";
import { ArrangementStrip } from "./components/ArrangementStrip/ArrangementStrip";
import { FxDialsPanel } from "./components/FxDials/FxDialsPanel";
import {
  useInstrumentStore,
  type SubdivisionMap,
} from "./state/instrumentStore";
import { useArrangementStore } from "./state/arrangementStore";
import { snapshotEditorState } from "./state/snapshot";
import { getAudioEngine } from "./audio/AudioEngine";
import { stepsPerBar, toneInterval } from "./constants/layout";
import type { TrackId } from "./constants/scale";
import {
  applySegmentSettings,
  scheduleDrumStep,
  scheduleMelodyStep,
  type StepSlice,
} from "./audio/scheduler";
import "./App.css";

const TRACKS: readonly TrackId[] = ["drums", "keys", "bass", "lead"];

function subdivisionsOf(slice: StepSlice): SubdivisionMap | null {
  const any = slice as unknown as { subdivisions?: SubdivisionMap };
  return any.subdivisions ?? null;
}

function intervalsFromSubdivisions(
  subs: SubdivisionMap,
): Record<TrackId, string> {
  return {
    drums: toneInterval(subs.drums),
    keys: toneInterval(subs.keys),
    bass: toneInterval(subs.bass),
    lead: toneInterval(subs.lead),
  };
}

export default function App() {
  const openFxPanel = useInstrumentStore((s) => s.openFxPanel);
  const activeTrack = useInstrumentStore((s) => s.activeTrack);
  const setStep = useInstrumentStore((s) => s.setStep);
  const resetSteps = useInstrumentStore((s) => s.resetSteps);
  const arrangementExpanded = useArrangementStore((s) => s.expanded);
  const lastSegmentIdRef = useRef<string | null>(null);

  const visibleTrack = arrangementExpanded ? null : activeTrack;

  // Wire per-track step callbacks once. Each callback reads current state
  // each tick, so subdivision / segment changes take effect on the next step.
  useEffect(() => {
    const engine = getAudioEngine();

    for (const track of TRACKS) {
      engine.setTrackStepCallback(track, (rawStep, time) => {
        const arr = useArrangementStore.getState();
        const songMode = arr.arrangementPlayMode && arr.order.length > 0;

        let slice: StepSlice;
        if (songMode) {
          const segId = arr.activeSegmentId ?? arr.order[0];
          const seg = arr.segments[segId];
          if (!seg) return;
          slice = seg as StepSlice;

          // Seeing a new segment id for the first time: apply its BPM + FX.
          if (track === "drums" && lastSegmentIdRef.current !== seg.id) {
            lastSegmentIdRef.current = seg.id;
            applySegmentSettings(engine, slice);
            const nextIntervals = intervalsFromSubdivisions(
              seg.subdivisions as SubdivisionMap,
            );
            engine.setTrackIntervals(nextIntervals);
            Tone.getDraw().schedule(() => {
              useArrangementStore.getState().setActiveSegment(seg.id);
            }, time);
          }
        } else {
          slice = useInstrumentStore.getState() as StepSlice;
          if (lastSegmentIdRef.current !== null) {
            lastSegmentIdRef.current = null;
            useArrangementStore.getState().setActiveSegment(null);
          }
        }

        const subs = subdivisionsOf(slice);
        if (!subs) return;
        const stepsInBar = stepsPerBar(subs[track]);
        const totalSteps = songMode
          ? stepsInBar * (slice as { bars?: number }).bars!
          : stepsInBar;
        const wrapped = totalSteps > 0 ? rawStep % totalSteps : rawStep;
        const cellStep = wrapped % stepsInBar;

        Tone.getDraw().schedule(() => setStep(track, cellStep), time);

        if (track === "drums") {
          scheduleDrumStep(engine, slice, cellStep, time);
        } else {
          scheduleMelodyStep(engine, slice, track, cellStep, time);
        }

        // Advance song-mode segment on the drums-track boundary.
        if (songMode && track === "drums" && wrapped === totalSteps - 1) {
          const currentArr = useArrangementStore.getState();
          const currentIdx = currentArr.order.indexOf(
            currentArr.activeSegmentId ?? currentArr.order[0],
          );
          const nextIdx =
            currentIdx < 0 ? 0 : (currentIdx + 1) % currentArr.order.length;
          const nextId = currentArr.order[nextIdx];
          if (nextId && nextId !== currentArr.activeSegmentId) {
            Tone.getDraw().schedule(() => {
              useArrangementStore.getState().setActiveSegment(nextId);
            }, time);
          }
        }
      });
    }

    return () => {
      for (const track of TRACKS) engine.setTrackStepCallback(track, null);
    };
  }, [setStep]);

  // Sync the engine's track intervals to the live subdivision state in editor
  // mode. Song mode overrides these on each segment boundary.
  useEffect(() => {
    const applyIntervals = (subs: SubdivisionMap) => {
      const engine = getAudioEngine();
      const { arrangementPlayMode, order } = useArrangementStore.getState();
      if (arrangementPlayMode && order.length > 0) return;
      engine.setTrackIntervals(intervalsFromSubdivisions(subs));
    };
    applyIntervals(useInstrumentStore.getState().subdivisions);
    const unsub = useInstrumentStore.subscribe((state, prev) => {
      if (state.subdivisions !== prev.subdivisions) {
        applyIntervals(state.subdivisions);
      }
    });
    return unsub;
  }, []);

  // Reset the song cursor / held segment when arrangement mode toggles or
  // playback stops.
  useEffect(() => {
    const unsub = useArrangementStore.subscribe((state, prev) => {
      if (state.arrangementPlayMode !== prev.arrangementPlayMode) {
        lastSegmentIdRef.current = null;
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = useInstrumentStore.subscribe((state, prev) => {
      if (prev.playing && !state.playing) {
        lastSegmentIdRef.current = null;
        resetSteps();
      }
    });
    return unsub;
  }, [resetSteps]);

  // While a segment is selected, mirror editor edits (FX, pattern, melodies,
  // mutes, subdivisions, BPM, scale) back into that segment so the arrangement
  // — and therefore WAV export — always reflects what's on screen. Fires
  // synchronously to avoid a pending write racing with export or segment
  // switches.
  useEffect(() => {
    const unsub = useInstrumentStore.subscribe((state, prev) => {
      if (
        state.pattern === prev.pattern &&
        state.melodyGrids === prev.melodyGrids &&
        state.fx === prev.fx &&
        state.trackMutes === prev.trackMutes &&
        state.drumMutes === prev.drumMutes &&
        state.subdivisions === prev.subdivisions &&
        state.bpm === prev.bpm &&
        state.rootPitch === prev.rootPitch &&
        state.blueFlat3 === prev.blueFlat3 &&
        state.blueFlat5 === prev.blueFlat5
      ) {
        return;
      }
      const { selectedSegmentId } = useArrangementStore.getState();
      if (!selectedSegmentId) return;
      useArrangementStore
        .getState()
        .replaceSegment(selectedSegmentId, snapshotEditorState());
    });
    return unsub;
  }, []);

  useEffect(() => {
    const handleKey = async (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        return;
      }
      e.preventDefault();
      const { playing, bpm, setPlaying } = useInstrumentStore.getState();
      const engine = getAudioEngine();
      if (playing) {
        engine.stop();
        setPlaying(false);
      } else {
        await engine.start(bpm);
        setPlaying(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <div className="app screen-flicker">
      <div className="crt-scanlines" aria-hidden="true" />
      <Transport />
      <div className="tracks">
        <div
          className={`track-row ${visibleTrack === "drums" ? "is-active" : "is-collapsed"}`}
        >
          <DrumTrack active={visibleTrack === "drums"} />
        </div>
        <div
          className={`track-row ${visibleTrack === "keys" ? "is-active" : "is-collapsed"}`}
        >
          <PianoRollTrack
            track="keys"
            label="KEYS"
            tone="yellow"
            active={visibleTrack === "keys"}
          />
        </div>
        <div
          className={`track-row ${visibleTrack === "bass" ? "is-active" : "is-collapsed"}`}
        >
          <PianoRollTrack
            track="bass"
            label="BASS"
            tone="red"
            active={visibleTrack === "bass"}
          />
        </div>
        <div
          className={`track-row ${visibleTrack === "lead" ? "is-active" : "is-collapsed"}`}
        >
          <PianoRollTrack
            track="lead"
            label="LEAD"
            tone="purple"
            active={visibleTrack === "lead"}
          />
        </div>
      </div>
      <ArrangementStrip />
      <AdvancedPanel />
      {openFxPanel && <FxDialsPanel track={openFxPanel} />}
    </div>
  );
}
